import { NumericDataPointDto } from '@/api/generator';
import { lightThemeColors } from '@/styles/vuetify';
import { PointOptionsObject } from 'highcharts';
import { isNumber, isUndefined, max, min, values } from 'lodash';
import { objectFromKeys } from './helper';

type LimitKey = 'upWarning' | 'lowWarning' | 'upError' | 'lowError';
export const limitOrder: LimitKey[] = ['lowError', 'lowWarning', 'upWarning', 'upError'];

type LimitedValue = { [k in LimitKey]?: number } & {
  value: number;
};

type BandKey = LimitKey | 'start' | 'end';

interface BandTemplate {
  id: BandId;
  from: BandKey;
  to: BandKey;
  color: string;
  title: string;
}

export type BandId = LimitKey | 'success';
const bandIds: BandId[] = ['lowError', 'lowWarning', 'success', 'upWarning', 'upError'];

export interface Band {
  id: BandId;
  from: number;
  to: number;
  color: string;
  title: string;
}

interface LimitRange {
  minLimit: number;
  maxLimit: number;
  range: number;
}

interface HasDeviationValue {
  deviationValue: number;
}

interface HasLimits {
  upperWarningLimit?: number | null;
  upperFailedLimit?: number | null;
  lowerFailedLimit?: number | null;
  lowerWarningLimit?: number | null;
}

export const bandColors: Record<BandId, string> = {
  lowError: lightThemeColors['error'],
  lowWarning: lightThemeColors['warning'],
  success: lightThemeColors['success'],
  upWarning: lightThemeColors['warning'],
  upError: lightThemeColors['error'],
};

const bandTemplates: { [k in 'lower' | 'center' | 'upper']: BandTemplate[] } = {
  lower: [
    {
      id: 'lowError',
      from: 'start',
      to: 'lowError',
      title: 'error',
      color: bandColors.lowError,
    },
    {
      id: 'lowWarning',
      from: 'lowError',
      to: 'lowWarning',
      title: 'warning',
      color: bandColors.lowWarning,
    },
  ],
  center: [
    {
      id: 'success',
      from: 'lowWarning',
      to: 'upWarning',
      title: 'success',
      color: bandColors.success,
    },
  ],
  upper: [
    {
      id: 'upWarning',
      from: 'upWarning',
      to: 'upError',
      title: 'warning',
      color: bandColors.upWarning,
    },
    {
      id: 'upError',
      from: 'upError',
      to: 'end',
      title: 'error',
      color: bandColors.upError,
    },
  ],
};

function getLimitedValue(data: HasDeviationValue, limits?: HasLimits): LimitedValue {
  return {
    value: data.deviationValue,
    lowError: limits?.lowerFailedLimit ?? undefined,
    lowWarning: limits?.lowerWarningLimit ?? undefined,
    upWarning: limits?.upperWarningLimit ?? undefined,
    upError: limits?.upperFailedLimit ?? undefined,
  };
}

export function applyPadding(
  limitRange: LimitRange | undefined,
  paddingPercent: number = 0,
): LimitRange | undefined {
  if (limitRange) {
    const hasRange = limitRange.maxLimit !== limitRange.minLimit;

    const padding = hasRange
      ? (limitRange.maxLimit - limitRange.minLimit) * (paddingPercent / 100)
      : 1;

    return {
      minLimit: limitRange.minLimit - padding,
      maxLimit: limitRange.maxLimit + padding,
      range: limitRange.range + 2 * padding,
    };
  }
}

export function calculateLimitsMinMax(
  data: LimitedValue[],
  paddingPercent?: number,
): LimitRange | undefined {
  const dataValues = data.flatMap(values).filter(isNumber);

  const minLimit = min(dataValues);
  const maxLimit = max(dataValues);

  if (isNumber(minLimit) && isNumber(maxLimit)) {
    return applyPadding(
      {
        minLimit,
        maxLimit,
        range: maxLimit - minLimit,
      },
      paddingPercent,
    );
  }
}

export function calculateDataPointsBands(
  dataPoints: NumericDataPointDto[],
  paddingPercent?: number,
) {
  const limitedValues = dataPoints.map((dataPoint) => getLimitedValue(dataPoint, dataPoint));

  const limitRange = calculateLimitsMinMax(limitedValues, paddingPercent);

  const dataPointBands = limitedValues.map(
    (limitedValue) => calculateBands(limitedValue, limitRange)?.bands,
  );

  const bands = objectFromKeys(bandIds, (id) =>
    dataPointBands
      .map((dataPointBand) => dataPointBand?.find((band) => band.id === id))
      .map<PointOptionsObject>((band, index) => ({
        x: dataPoints[index].creationTime,
        low: band?.from,
        high: band?.to,
        color: bandColors[id],
      })),
  );

  return {
    bands,
    ...limitRange,
  };
}

export function calculateBands(data: LimitedValue, limitRange?: LimitRange) {
  const range = limitRange ?? calculateLimitsMinMax([data]);

  if (isUndefined(range)) {
    return undefined;
  }

  const { minLimit: start, maxLimit: end } = range;

  const sparseBandLimits: { [k in BandKey]?: number | undefined } = {
    start,
    ...data,
    end,
  };

  const lowerBands = bandTemplates.lower.reduce<{
    last: number;
    bands: Band[];
  }>(
    ({ last, bands }, template) => {
      const limit = sparseBandLimits[template.to];

      return limit === undefined
        ? { last, bands }
        : {
            last: limit,
            bands: [...bands, { ...template, from: last, to: limit }],
          };
    },
    {
      last: start,
      bands: [],
    },
  ).bands;

  const upperBands = bandTemplates.upper
    .toReversed()
    .reduce<{
      last: number;
      bands: Band[];
    }>(
      ({ last, bands }, template) => {
        const limit = sparseBandLimits[template.from];

        return limit === undefined
          ? { last, bands }
          : {
              last: limit,
              bands: [...bands, { ...template, from: limit, to: last }],
            };
      },
      {
        last: end,
        bands: [],
      },
    )
    .bands.toReversed();

  const centerBand: Band = {
    ...bandTemplates.center[0],
    from: lowerBands.at(-1)?.to ?? start,
    to: upperBands.at(0)?.from ?? end,
  };

  return {
    bands: [...lowerBands, centerBand, ...upperBands],
    ...range,
  };
}
